import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShoppingBag, AlertTriangle, CheckCircle, XCircle } from "lucide-react";

export default function MarketplaceTermsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-[var(--liturgical-primary)] hover:underline mb-8 font-['Nunito']">
          <ArrowLeft className="w-4 h-4" /> Back to Parish
        </Link>
        
        <div className="bg-white rounded-xl shadow-sm border border-[#E5E0D5] p-8 md:p-12">
          <div className="flex items-center gap-3 mb-6">
            <ShoppingBag className="w-8 h-8 text-[var(--liturgical-primary)]" />
            <h1 className="font-['Crimson_Text'] text-3xl md:text-4xl font-bold text-[#2D2A26]">Marketplace Terms</h1>
          </div>
          
          <p className="text-sm text-[#6B665E] font-['Nunito'] mb-8">Last Updated: February 25, 2026</p>
          
          <div className="bg-[#FFF8E7] border border-[#F5E6C8] rounded-lg p-4 mb-8 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-[#8B6914] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-[#8B6914] font-semibold font-['Nunito']">Important Notice</p>
              <p className="text-[#8B6914] text-sm font-['Nunito']">
                Parish is a directory service only. We do not process payments, handle shipping, or act as a party to any transaction. All transactions are conducted directly between buyers and sellers.
              </p>
            </div>
          </div>
          
          <div className="prose prose-slate max-w-none font-['Nunito'] text-[#2D2A26]">
            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">1. Overview</h2>
              <p className="text-[#4A4A4A] leading-relaxed mb-4">
                The Parish Marketplace is a community feature that allows members to list items and services for sale or trade. Our marketplace functions similarly to a classified ads board—we provide the platform for buyers and sellers to connect, but we are not involved in the actual transactions.
              </p>
              <p className="text-[#4A4A4A] leading-relaxed">
                By using the Parish Marketplace, you agree to these Marketplace Terms in addition to our general <Link to="/terms" className="text-[var(--liturgical-primary)] hover:underline">Terms of Service</Link> and <Link to="/community-guidelines" className="text-[var(--liturgical-primary)] hover:underline">Community Guidelines</Link>.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">2. How the Marketplace Works</h2>
              <div className="space-y-4">
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-[var(--liturgical-primary)] text-white flex items-center justify-center flex-shrink-0 text-sm font-bold">1</div>
                  <div>
                    <p className="font-semibold text-[#2D2A26]">List Your Item or Service</p>
                    <p className="text-[#4A4A4A] text-sm">Sellers create listings with descriptions, photos, prices, and location information.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-[var(--liturgical-primary)] text-white flex items-center justify-center flex-shrink-0 text-sm font-bold">2</div>
                  <div>
                    <p className="font-semibold text-[#2D2A26]">Buyers Contact Sellers</p>
                    <p className="text-[#4A4A4A] text-sm">Interested buyers use the "Message Seller" button to communicate directly with the seller.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-[var(--liturgical-primary)] text-white flex items-center justify-center flex-shrink-0 text-sm font-bold">3</div>
                  <div>
                    <p className="font-semibold text-[#2D2A26]">Arrange the Transaction</p>
                    <p className="text-[#4A4A4A] text-sm">Buyer and seller agree on payment method, delivery/pickup, and other terms.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-[var(--liturgical-primary)] text-white flex items-center justify-center flex-shrink-0 text-sm font-bold">4</div>
                  <div>
                    <p className="font-semibold text-[#2D2A26]">Complete the Exchange</p>
                    <p className="text-[#4A4A4A] text-sm">Meet in person or arrange shipping. Payment and delivery are handled between the parties.</p>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">3. Seller Responsibilities</h2>
              <p className="text-[#4A4A4A] leading-relaxed mb-4">As a seller, you agree to:</p>
              <ul className="list-disc pl-6 text-[#4A4A4A] space-y-2">
                <li>Provide accurate, honest descriptions of your items or services</li>
                <li>Include clear, unaltered photos that accurately represent the item</li>
                <li>Set fair, transparent prices</li>
                <li>Disclose any defects, damage, or issues with items</li>
                <li>Respond to buyer inquiries in a timely manner</li>
                <li>Honor agreed-upon terms with buyers</li>
                <li>Only list items you have the right to sell</li>
                <li>Comply with all applicable laws regarding the sale of your items</li>
                <li>Mark listings as sold once the transaction is complete</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">4. Buyer Responsibilities</h2>
              <p className="text-[#4A4A4A] leading-relaxed mb-4">As a buyer, you agree to:</p>
              <ul className="list-disc pl-6 text-[#4A4A4A] space-y-2">
                <li>Read listings carefully before contacting sellers</li>
                <li>Ask questions to clarify any uncertainties before purchasing</li>
                <li>Honor commitments to purchase after agreeing with the seller</li>
                <li>Make payments as agreed with the seller</li>
                <li>Inspect items upon receipt and communicate any issues promptly</li>
                <li>Leave honest, fair reviews after transactions</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" /> 5. Allowed Items
              </h2>
              <p className="text-[#4A4A4A] leading-relaxed mb-4">You may list:</p>
              <ul className="list-disc pl-6 text-[#4A4A4A] space-y-2">
                <li>Religious items (rosaries, medals, books, art, etc.)</li>
                <li>Household items and furniture</li>
                <li>Clothing and accessories</li>
                <li>Electronics and appliances</li>
                <li>Vehicles (with proper documentation)</li>
                <li>Handmade goods and crafts</li>
                <li>Services (tutoring, lawn care, childcare, etc.)</li>
                <li>Event tickets (at face value)</li>
                <li>Books, music, and media</li>
                <li>Sporting goods and outdoor equipment</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4 flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-600" /> 6. Prohibited Items
              </h2>
              <p className="text-[#4A4A4A] leading-relaxed mb-4">The following may NOT be listed:</p>
              <ul className="list-disc pl-6 text-[#4A4A4A] space-y-2">
                <li><strong>Weapons & Ammunition:</strong> Firearms, knives, explosives, or ammunition</li>
                <li><strong>Illegal Items:</strong> Drugs, drug paraphernalia, counterfeit goods, stolen property</li>
                <li><strong>Adult Content:</strong> Pornography, sexual services, adult toys</li>
                <li><strong>Alcohol & Tobacco:</strong> Alcoholic beverages, cigarettes, vaping products</li>
                <li><strong>Prescription Items:</strong> Medications, medical devices requiring prescription</li>
                <li><strong>Hazardous Materials:</strong> Chemicals, flammable substances, toxic materials</li>
                <li><strong>Live Animals:</strong> Pets or livestock (adoption referrals to shelters allowed)</li>
                <li><strong>Human Parts:</strong> Organs, blood, or bodily fluids</li>
                <li><strong>Gambling:</strong> Lottery tickets, gambling services</li>
                <li><strong>Multi-Level Marketing:</strong> MLM products or recruitment</li>
                <li><strong>Anti-Catholic Items:</strong> Materials that mock or attack the Catholic faith</li>
                <li><strong>Recalled Items:</strong> Products subject to safety recalls</li>
                <li><strong>Scalped Tickets:</strong> Event tickets at inflated prices</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">7. Safety Guidelines</h2>
              <p className="text-[#4A4A4A] leading-relaxed mb-4">
                Your safety is important. When meeting to complete a transaction:
              </p>
              <div className="bg-[#F9F7F2] rounded-lg p-4 space-y-3">
                <p className="text-[#4A4A4A]"><strong>✓ Meet in Public Places:</strong> Choose well-lit, busy locations like coffee shops, shopping centers, or police station parking lots.</p>
                <p className="text-[#4A4A4A]"><strong>✓ Bring Someone:</strong> Consider bringing a friend or family member, especially for high-value items.</p>
                <p className="text-[#4A4A4A]"><strong>✓ Meet During Daylight:</strong> Schedule meetings during daytime hours when possible.</p>
                <p className="text-[#4A4A4A]"><strong>✓ Trust Your Instincts:</strong> If something feels wrong, don't proceed with the transaction.</p>
                <p className="text-[#4A4A4A]"><strong>✓ Verify Before Paying:</strong> Inspect items before completing payment.</p>
                <p className="text-[#4A4A4A]"><strong>✓ Use Secure Payment:</strong> Be cautious of unusual payment requests. Cash or well-known payment apps are typically safest.</p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">8. Disputes</h2>
              <p className="text-[#4A4A4A] leading-relaxed mb-4">
                Since Parish does not participate in transactions, we cannot resolve disputes between buyers and sellers regarding:
              </p>
              <ul className="list-disc pl-6 text-[#4A4A4A] space-y-2">
                <li>Quality or condition of items</li>
                <li>Payment disputes</li>
                <li>Shipping or delivery issues</li>
                <li>Refunds or returns</li>
              </ul>
              <p className="text-[#4A4A4A] leading-relaxed mt-4">
                We encourage buyers and sellers to work out disagreements directly. If you believe someone has violated our Community Guidelines or engaged in fraudulent activity, please report them.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">9. Reviews</h2>
              <p className="text-[#4A4A4A] leading-relaxed mb-4">
                Reviews help build trust in our community. When leaving reviews:
              </p>
              <ul className="list-disc pl-6 text-[#4A4A4A] space-y-2">
                <li>Be honest and fair in your assessment</li>
                <li>Base reviews on actual transactions</li>
                <li>Avoid personal attacks or inappropriate language</li>
                <li>Do not leave fake reviews (positive or negative)</li>
                <li>Do not offer or accept compensation for reviews</li>
              </ul>
              <p className="text-[#4A4A4A] leading-relaxed mt-4">
                We reserve the right to remove reviews that violate these guidelines.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">10. Limitation of Liability</h2>
              <p className="text-[#4A4A4A] leading-relaxed mb-4">
                Parish provides the marketplace platform as a service to our community. We do not:
              </p>
              <ul className="list-disc pl-6 text-[#4A4A4A] space-y-2">
                <li>Verify the identity of buyers or sellers</li>
                <li>Inspect, endorse, or guarantee any items listed</li>
                <li>Process payments or handle funds</li>
                <li>Provide shipping or delivery services</li>
                <li>Guarantee the completion or satisfaction of any transaction</li>
              </ul>
              <p className="text-[#4A4A4A] leading-relaxed mt-4">
                You use the marketplace at your own risk. Parish is not liable for any losses, damages, or injuries resulting from marketplace transactions.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">11. Fees</h2>
              <p className="text-[#4A4A4A] leading-relaxed">
                The Parish Marketplace is currently free to use. We do not charge listing fees or transaction fees. We reserve the right to introduce fees in the future with advance notice.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">12. Enforcement</h2>
              <p className="text-[#4A4A4A] leading-relaxed mb-4">
                Violations of these Marketplace Terms may result in:
              </p>
              <ul className="list-disc pl-6 text-[#4A4A4A] space-y-2">
                <li>Removal of listings</li>
                <li>Temporary suspension of marketplace privileges</li>
                <li>Permanent ban from the marketplace</li>
                <li>Account termination</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-['Crimson_Text'] text-xl font-bold text-[#2D2A26] mb-4">13. Contact Us</h2>
              <p className="text-[#4A4A4A] leading-relaxed">
                Questions about the Marketplace Terms? Contact us at:
              </p>
              <div className="bg-[#F9F7F2] rounded-lg p-4 mt-4">
                <p className="text-[#4A4A4A]"><strong>Email:</strong> <a href="mailto:marketplace@parish.com" className="text-[var(--liturgical-primary)] hover:underline">marketplace@parish.com</a></p>
              </div>
            </section>
          </div>
        </div>
        
        <div className="mt-8 text-center">
          <p className="text-sm text-[#6B665E] font-['Nunito']">
            See also: <Link to="/privacy" className="text-[var(--liturgical-primary)] hover:underline">Privacy Policy</Link> · <Link to="/terms" className="text-[var(--liturgical-primary)] hover:underline">Terms of Service</Link> · <Link to="/cookies" className="text-[var(--liturgical-primary)] hover:underline">Cookie Policy</Link> · <Link to="/community-guidelines" className="text-[var(--liturgical-primary)] hover:underline">Community Guidelines</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
